import "./App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { useEffect, useState, useCallback, lazy, Suspense } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SessionProvider } from "./context/SessionContext";
import { ThemeProvider } from "./context/ThemeContext";
import { MusicKitProvider } from "./context/MusicKitContext";
import { PlaylistMusicProvider } from "./context/PlaylistMusicContext";
import { DropboxProvider } from "./context/DropboxContext";
import { BoxProvider } from "./context/BoxContext";
import { VerifiedArtistProvider } from "./context/VerifiedArtistContext";
import { SubscriptionProvider } from "./context/SubscriptionContext";
import { TapInEventProvider, useTapInEvent } from "./context/TapInEventContext";
import UpgradeModal from "./components/UpgradeModal";
import VerifiedCelebration from "./components/shared/VerifiedCelebration";
import EventGuestTapIn from "./components/events/EventGuestTapIn";
import { ToastContainer } from "./components/shared/Toast";
import { TapInToastProvider } from "./components/shared/TapInToast";
import { Toaster } from "sonner";
import ErrorBoundary from "./components/shared/ErrorBoundary";
import usePageTitle from "./hooks/usePageTitle";
import AppLayout from "./components/shared/AppLayout";

// Global MusicKit error suppression - prevents error overlays
if (typeof window !== 'undefined') {
  const suppressedPatterns = [
    'descriptor', 'MusicKit', 'musickit', 'MEDIA_KEY',
    'play() request', 'AbortError', 'NotAllowedError'
  ];
  
  // Suppress errors in development mode React error overlay
  window.addEventListener('error', (event) => {
    const msg = event.message || '';
    if (suppressedPatterns.some(p => msg.toLowerCase().includes(p.toLowerCase()))) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return false;
    }
  }, true);
}

// Marketing Pages - Load immediately (small)
import HomePage from "./pages/HomePage";
import LandingPage from "./pages/LandingPage";
import ComingSoonPage from "./pages/ComingSoonPage";

// Lazy load heavy pages for better mobile performance
const SessionsPage = lazy(() => import("./pages/SessionsPage"));
const PlaylistsPage = lazy(() => import("./pages/PlaylistsPage"));
const PartnersPage = lazy(() => import("./pages/PartnersPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const StatusPage = lazy(() => import("./pages/StatusPage"));

// Partner Pricing Pages
const PartnerPricingPage = lazy(() => import("./pages/PartnerPricingPage"));

// Private Portal Pages
const PrivatePortalPage = lazy(() => import("./pages/PrivatePortalPage"));
const PortalInvitePage = lazy(() => import("./pages/PortalInvitePage"));

// Legal Pages - Lazy load
const TermsOfService = lazy(() => import("./pages/legal/TermsOfService"));
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));

// Pitch Pages - Lazy load
const DriverPitchPage = lazy(() => import("./pages/pitch/DriverPitchPage"));
const GinJuicePitchDeck = lazy(() => import("./pages/pitch/GinJuicePitchDeck"));
const TapInUpdateDeck = lazy(() => import("./pages/pitch/TapInUpdateDeck"));
const TapInPlanPage = lazy(() => import("./pages/pitch/TapInPlanPage"));

// Pilot Pages - Lazy load
const PilotInvitePage = lazy(() => import("./pages/PilotInvitePage"));
const PilotPortalPage = lazy(() => import("./pages/PilotPortalPage"));

// Public Profile Page - Lazy load
const PublicProfilePage = lazy(() => import("./pages/PublicProfilePage"));

// Admin Pages - Lazy load
const AdminLoginPage = lazy(() => import("./pages/admin/AdminLoginPage"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsersPage = lazy(() => import("./pages/admin/AdminUsersPage"));
const AdminPortalsPage = lazy(() => import("./pages/admin/AdminPortalsPage"));
const AdminProductPage = lazy(() => import("./pages/admin/AdminProductPage"));
const AdminMessagesPage = lazy(() => import("./pages/admin/AdminMessagesPage"));
const AdminAnalyticsPage = lazy(() => import("./pages/admin/AdminAnalyticsPage"));
const AdminFinancialsPage = lazy(() => import("./pages/admin/AdminFinancialsPage"));
const AdminFilesPage = lazy(() => import("./pages/admin/AdminFilesPage"));
const AdminAccessPage = lazy(() => import("./pages/admin/AdminAccessPage"));
const AdminSafetyPage = lazy(() => import("./pages/admin/AdminSafetyPage"));
const AdminVerifiedArtistsPage = lazy(() => import("./pages/admin/AdminVerifiedArtistsPage"));
const AdminRecapPage = lazy(() => import("./pages/admin/AdminRecapPage"));
const AdminHelpPage = lazy(() => import("./pages/admin/AdminHelpPage"));

// Test Pages
const TestAppleMusicModal = lazy(() => import("./pages/TestAppleMusicModal"));

// Auth & Onboarding - Load immediately (critical path)
import AuthPage from "./pages/auth/AuthPage";
import OnboardingFlow from "./pages/onboarding/OnboardingFlow";

// App Pages - Lazy load for better mobile performance
const DashboardPageV2 = lazy(() => import("./pages/app/DashboardPageV2"));
const ProfilePage = lazy(() => import("./pages/app/ProfilePage"));
const ProfileEditPage = lazy(() => import("./pages/app/ProfileEditPage"));
const SettingsPage = lazy(() => import("./pages/app/SettingsPage"));
const SubscriptionSuccessPage = lazy(() => import("./pages/app/SubscriptionSuccessPage"));
const NotificationsPage = lazy(() => import("./pages/app/NotificationsPage"));
const MessagesPage = lazy(() => import("./pages/app/MessagesPage"));
const ConversationPage = lazy(() => import("./pages/app/ConversationPage"));
const NewMessagePage = lazy(() => import("./pages/app/NewMessagePage"));
const JoinSessionPage = lazy(() => import("./pages/app/JoinSessionPage"));
const DevPlaylistsPage = lazy(() => import("./pages/app/DevPlaylistsPage"));
const MyPlaylistsPage = lazy(() => import("./pages/app/MyPlaylistsPage"));
const CreatePlaylistPage = lazy(() => import("./pages/app/CreatePlaylistPage"));
const PlaylistDetailPage = lazy(() => import("./pages/app/PlaylistDetailPage"));

// Event Playlist Pages (BETA)
const EventPlaylistPage = lazy(() => import("./pages/app/EventPlaylistPage"));
const EventDetailPage = lazy(() => import("./pages/app/EventDetailPage"));
const EventJoinPage = lazy(() => import("./pages/app/EventJoinPage"));

// Loading spinner for lazy-loaded pages
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="flex flex-col items-center gap-3">
      <div className="w-10 h-10 border-3 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
      <p className="text-gray-500 text-sm">Loading...</p>
    </div>
  </div>
);

// Scroll to top on route change + update page title
function ScrollToTop() {
  const { pathname } = useLocation();
  usePageTitle(); // Update page title based on route
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, isOnboarded, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    // Store the intended destination for deep linking
    const intendedPath = location.pathname + location.search;
    if (intendedPath !== '/app' && intendedPath !== '/app/') {
      localStorage.setItem('tapInRedirectAfterAuth', intendedPath);
    }
    return <Navigate to="/auth" replace />;
  }

  if (!isOnboarded) {
    // Keep the redirect stored - will be used after onboarding
    return <Navigate to="/onboarding" replace />;
  }

  return children;
}

// Auth route wrapper
function AuthRoute({ children }) {
  const { isAuthenticated, isOnboarded, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated && isOnboarded) {
    // Check for stored redirect path (deep link)
    const redirectPath = localStorage.getItem('tapInRedirectAfterAuth');
    if (redirectPath) {
      localStorage.removeItem('tapInRedirectAfterAuth');
      return <Navigate to={redirectPath} replace />;
    }
    return <Navigate to="/app" replace />;
  }

  if (isAuthenticated && !isOnboarded) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
}

// Onboarding route wrapper
function OnboardingRoute({ children }) {
  const { isAuthenticated, isOnboarded, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  if (isOnboarded) {
    // Check for stored redirect path (deep link)
    const redirectPath = localStorage.getItem('tapInRedirectAfterAuth');
    if (redirectPath) {
      localStorage.removeItem('tapInRedirectAfterAuth');
      return <Navigate to={redirectPath} replace />;
    }
    return <Navigate to="/app" replace />;
  }

  return children;
}

// Deep Link Session Route - Handles /session/:sessionId
function SessionDeepLink() {
  const { sessionId } = useParams();
  const { isAuthenticated, isOnboarded, loading } = useAuth();

  // Store session ID for redirect after auth
  useEffect(() => {
    if (sessionId) {
      localStorage.setItem('tapInPendingSession', sessionId);
    }
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  // Allow viewing without auth - redirect to public view
  // The page will handle prompting signup when user tries to tap in
  return <Navigate to={`/app/session/${sessionId}?public=true`} replace />;
}

// Deep Link Playlist Route - Handles /playlist/:playlistId
function PlaylistDeepLink() {
  const { playlistId } = useParams();
  
  useEffect(() => {
    if (playlistId) {
      localStorage.setItem('tapInPendingPlaylist', playlistId);
    }
  }, [playlistId]);

  // Allow viewing without auth - redirect to public view
  return <Navigate to={`/app/playlist/${playlistId}?public=true`} replace />;
}


// Global popup for guest Tap In a Song - renders over any page
function TapInEventPopup() {
  const { tapInEventId, tapInEvent, closeTapIn } = useTapInEvent();
  if (!tapInEventId || !tapInEvent) return null;
  return (
    <EventGuestTapIn
      event={tapInEvent}
      eventId={tapInEventId}
      onClose={closeTapIn}
    />
  );
}

// Main app content with routing
function AppContent() {
  const location = useLocation();
  const pathname = location.pathname;
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  
  // Check for deep link on initial load (before state initialization)
  const isDeepLink = pathname.startsWith('/session/') || pathname.startsWith('/event/') || pathname.startsWith('/playlist/') || pathname.startsWith('/event-playlist/');
  
  // Deep link handling for pending sessions/events
  useEffect(() => {
    if (pathname.startsWith('/session/')) {
      const sessionId = pathname.split('/session/')[1];
      if (sessionId) localStorage.setItem('tapInPendingSession', sessionId);
    } else if (pathname.startsWith('/event/')) {
      const eventId = pathname.split('/event/')[1];
      if (eventId) localStorage.setItem('tapInPendingEvent', eventId);
    }
  }, [pathname]);

  // Access gate - sessionStorage persists only for current browser session
  const [hasAccess, setHasAccess] = useState(() => {
    if (isDeepLink) {
      sessionStorage.setItem('tapInAccess', 'granted');
      return true;
    }
    return sessionStorage.getItem('tapInAccess') === 'granted';
  });

  const handleAccessGranted = useCallback(() => {
    sessionStorage.setItem('tapInAccess', 'granted');
    setHasAccess(true);
  }, []);

  // Paths that bypass the access gate
  const publicPaths = ['/terms', '/privacy', '/pitch', '/drivers', '/deck', '/tapinupdate', '/tapinplan', '/test-apple-modal', '/admin', '/partners', '/pricing', '/portal', '/pilot', '/app/playlist', '/app/session'];
  const isPublicPath = publicPaths.some(path => pathname.startsWith(path));
  const pathSegments = pathname.split('/').filter(Boolean);
  const isPotentialProfilePath = pathSegments.length === 1 && !['auth', 'app', 'onboarding'].includes(pathSegments[0]);

  // Show loading while checking auth state
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  // Coming Soon gate for unauthenticated visitors
  const isLoggedIn = !!user;
  if (!isLoggedIn && !hasAccess && !isPublicPath && !isDeepLink && !isPotentialProfilePath) {
    return <ComingSoonPage onAccessGranted={handleAccessGranted} showAccessCode={true} />;
  }

  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Marketing Pages */}
          <Route path="/" element={<HomePage />} />
          <Route path="/sessions" element={<SessionsPage />} />
          <Route path="/playlists" element={<PlaylistsPage />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/partners/:category" element={<PartnerPricingPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/status" element={<StatusPage />} />
          
          {/* Private Portal Pages */}
          <Route path="/portal/invite/:token" element={<PortalInvitePage />} />
          <Route path="/portal/:portalSlug" element={<PrivatePortalPage />} />
          
          {/* Legal Pages */}
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          
          {/* Pitch Pages */}
          <Route path="/drivers" element={<DriverPitchPage />} />
          <Route path="/pitch/uber" element={<DriverPitchPage />} />
          <Route path="/pitch/drivers" element={<DriverPitchPage />} />
          <Route path="/pitch/ginjuice" element={<GinJuicePitchDeck />} />
          <Route path="/pitch/dre" element={<GinJuicePitchDeck />} />
          <Route path="/deck" element={<GinJuicePitchDeck />} />
          <Route path="/tapinupdate" element={<TapInUpdateDeck />} />
          <Route path="/tapinplan" element={<TapInPlanPage />} />
          
          {/* Pilot Invite Page */}
          <Route path="/pilot/invite/:token" element={<PilotInvitePage />} />
          <Route path="/pilot/:pilotType/:pilotSlug" element={<PilotPortalPage />} />
          
          {/* Admin Panel Routes */}
          <Route path="/admin" element={<AdminLoginPage />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsersPage />} />
            <Route path="portals" element={<AdminPortalsPage />} />
            <Route path="product" element={<AdminProductPage />} />
            <Route path="messages" element={<AdminMessagesPage />} />
            <Route path="analytics" element={<AdminAnalyticsPage />} />
            <Route path="financials" element={<AdminFinancialsPage />} />
            <Route path="files" element={<AdminFilesPage />} />
            <Route path="access" element={<AdminAccessPage />} />
            <Route path="safety" element={<AdminSafetyPage />} />
            <Route path="verified-artists" element={<AdminVerifiedArtistsPage />} />
            <Route path="recap" element={<AdminRecapPage />} />
            <Route path="help" element={<AdminHelpPage />} />
          </Route>
          
          {/* Test Page - Apple Music Already Linked Modal Preview */}
          <Route path="/test-apple-modal" element={<TestAppleMusicModal />} />
          
          {/* Auth Pages */}
          <Route 
            path="/auth" 
            element={
              <AuthRoute>
                <AuthPage onAuthSuccess={() => {
                  // Check for stored deep link path
                  const redirectPath = localStorage.getItem('tapInRedirectAfterAuth');
                  if (redirectPath) {
                    localStorage.removeItem('tapInRedirectAfterAuth');
                    window.location.replace(redirectPath);
                  } else {
                    window.location.replace('/app');
                  }
                }} />
              </AuthRoute>
            } 
          />
          
          {/* Onboarding */}
          <Route 
            path="/onboarding" 
            element={
              <OnboardingRoute>
                <OnboardingFlow onComplete={() => {
                  // Always go to home screen first (for Apple Music connect)
                  // Pending event will be picked up after Apple Music is handled
                  localStorage.removeItem('tapInRedirectAfterAuth');
                  window.location.href = '/app';
                }} />
              </OnboardingRoute>
            } 
          />
          
          {/* App Pages (Protected) */}
          <Route 
            path="/app" 
            element={
              <ProtectedRoute>
                <AppLayout>
                  <DashboardPageV2 />
                </AppLayout>
              </ProtectedRoute>
            } 
          />
        
        {/* Profile Page */}
        <Route 
          path="/app/profile" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <ProfilePage />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/app/profile/edit" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <ProfileEditPage />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/app/profile/:userId" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <ProfilePage />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/app/settings" 
          element={
            <ProtectedRoute>
              <MusicKitProvider>
                <SessionProvider>
                  <AppLayout>
                    <SettingsPage />
                  </AppLayout>
                </SessionProvider>
              </MusicKitProvider>
            </ProtectedRoute>
          } 
        />
        
        {/* Subscription Success Page */}
        <Route 
          path="/app/subscription/success" 
          element={
            <ProtectedRoute>
              <SubscriptionSuccessPage />
            </ProtectedRoute>
          } 
        />

        {/* Notifications Page */}
        <Route 
          path="/app/notifications" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <NotificationsPage />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        
        {/* Messages/DM Pages */}
        <Route 
          path="/app/messages" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <MessagesPage />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/app/messages/new" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <NewMessagePage />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/app/messages/new/:userId" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <NewMessagePage />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/app/messages/with/:userId" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <ConversationPage />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/app/messages/:conversationId" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <ConversationPage />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        
        {/* Session Page - for accepting session invites (public access for QR codes) */}
        <Route 
          path="/app/session/:sessionId" 
          element={
            <AppLayout>
              <DashboardPageV2 />
            </AppLayout>
          } 
        />
        
        {/* Join Session Page - browse live sessions */}
        <Route 
          path="/app/sessions/join" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <JoinSessionPage />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        
        {/* My Playlists Page - View all user's playlists */}
        <Route 
          path="/app/myplaylists" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <MyPlaylistsPage />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        
        {/* Create Playlist Page - Private testing (web) / Public (mobile) */}
        <Route 
          path="/app/createaplaylist" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <CreatePlaylistPage />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        
        {/* Playlist Detail Page - public access for QR codes */}
        <Route 
          path="/app/playlist/:playlistId" 
          element={
            <AppLayout>
              <PlaylistDetailPage />
            </AppLayout>
          } 
        />
        
        {/* Dev Playlists Page - Hidden route for testing */}
        <Route 
          path="/app/dev/playlists" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <DevPlaylistsPage />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        
        {/* Dev Playlist Detail Page */}
        <Route 
          path="/app/dev/playlists/:playlistId" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <DevPlaylistsPage />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        
        {/* Event Playlist Pages (BETA) */}
        <Route 
          path="/event-playlist" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <EventPlaylistPage />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/event-playlist/:eventId" 
          element={
            <ProtectedRoute>
              <AppLayout>
                <EventDetailPage />
              </AppLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/event/:eventId" 
          element={<EventJoinPage />} 
        />
        
        {/* Deep Link Session Route - bypasses access code */}
        <Route 
          path="/session/:sessionId" 
          element={<SessionDeepLink />} 
        />
        
        {/* Deep Link Playlist Route - bypasses access code */}
        <Route 
          path="/playlist/:playlistId" 
          element={<PlaylistDeepLink />} 
        />
        
        {/* Public Profile Route - /:username (must be after all other routes) */}
        <Route path="/:username" element={<PublicProfilePage />} />
        
        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <TapInToastProvider>
          <ToastContainer>
            <BrowserRouter>
              <ThemeProvider>
                <AuthProvider>
                  <MusicKitProvider>
                    <SessionProvider>
                      <PlaylistMusicProvider>
                        <DropboxProvider>
                          <BoxProvider>
                          <VerifiedArtistProvider>
                            <SubscriptionProvider>
                              <TapInEventProvider>
                                <UpgradeModal />
                                <VerifiedCelebration />
                                <TapInEventPopup />
                                <AppContent />
                              </TapInEventProvider>
                            </SubscriptionProvider>
                          </VerifiedArtistProvider>
                          </BoxProvider>
                        </DropboxProvider>
                      </PlaylistMusicProvider>
                    </SessionProvider>
                  </MusicKitProvider>
                </AuthProvider>
              </ThemeProvider>
            </BrowserRouter>
          </ToastContainer>
        </TapInToastProvider>
        <Toaster position="top-center" richColors />
      </ErrorBoundary>
    </div>
  );
}

export default App;
// Force rebuild Sun Feb 22 08:35:17 UTC 2026
